import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { FacePoint, PromoImage } from './promo';

interface PromoImgProps {
  image: PromoImage;
  /** The rendered width, for srcset (e.g. "(min-width: 1024px) 240px, 46vw"). */
  sizes: string;
  /** Above the fold: load now, at high priority. Everything else is lazy. */
  eager?: boolean;
  /** Overrides the image's own alt ("" when a surrounding control names it). */
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onLoad?: () => void;
}

/** One promo picture: webp srcset, intrinsic size, lazy below the fold, localized alt. */
export function PromoImg({ image, sizes, eager = false, alt, className, style, onLoad }: PromoImgProps) {
  const { t } = useTranslation();
  return (
    <img
      src={image.src}
      srcSet={image.srcSet}
      sizes={sizes}
      width={image.width}
      height={image.height}
      alt={alt ?? t(image.altKey)}
      loading={eager ? 'eager' : 'lazy'}
      fetchPriority={eager ? 'high' : undefined}
      decoding="async"
      draggable={false}
      className={className}
      style={style}
      onLoad={onLoad}
    />
  );
}

interface DrawnFaceProps {
  image: PromoImage;
  face: FacePoint;
  /** The frame's rendered width times face.zoom, for srcset. */
  sizes: string;
  alt?: string;
  className?: string;
  onLoad?: () => void;
}

/**
 * A square close-up of one face in an illustration: the picture drawn
 * face.zoom times the frame's size and shifted so the face sits in the
 * middle. The frame is square, so percentages work on both axes.
 */
export function DrawnFace({ image, face, sizes, alt, className, onLoad }: DrawnFaceProps) {
  const scale = face.zoom * 100;
  return (
    <span className={cn('relative block aspect-square overflow-hidden', className)}>
      <PromoImg
        image={image}
        sizes={sizes}
        alt={alt}
        onLoad={onLoad}
        className="absolute max-w-none select-none"
        style={{
          width: `${scale}%`,
          height: `${scale}%`,
          left: `${50 - face.x * scale}%`,
          top: `${50 - face.y * scale}%`,
        }}
      />
    </span>
  );
}
